// hooks/useFileProcessing.ts
import { useState } from 'react';
import { submitAssistantFile } from '../api/api';

export type FileStepStatus = 'pending' | 'active' | 'complete' | 'error';

export interface FileProgress {
    filename: string;
    currentStep: 'uploading' | 'processing' | 'indexing' | 'complete' | 'error';
    steps: {
        uploading: FileStepStatus;
        processing: FileStepStatus;
        indexing: FileStepStatus;
    };
    errorMessage?: string;
}

export const useFileProcessing = () => {
    const [currentFileProgress, setCurrentFileProgress] = useState<FileProgress | null>(null);
    const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
    const [totalFiles, setTotalFiles] = useState<number>(0);

    const initializeFileProgress = (filename: string): FileProgress => {
        return {
            filename,
            currentStep: 'uploading',
            steps: {
                uploading: 'pending',
                processing: 'pending',
                indexing: 'pending'
            }
        };
    };

    const startNewFile = (filename: string, index: number, total: number) => {
        setCurrentFileIndex(index);
        setTotalFiles(total);
        setCurrentFileProgress(initializeFileProgress(filename));
    };

    const updateCurrentFileProgress = (updates: Partial<FileProgress>) => {
        setCurrentFileProgress(prev => prev ? { ...prev, ...updates } : null);
    };

    const setStepStatus = (
        step: keyof FileProgress['steps'],
        status: FileStepStatus
    ) => {
        setCurrentFileProgress(prev =>
            prev ? {
                ...prev,
                steps: { ...prev.steps, [step]: status }
            } : null
        );
    };

    const uploadDocument = async (
        assistantId: string,
        documentFile: File,
        index: number,
        total: number,
        idToken?: string
    ): Promise<{ success: boolean; error?: string }> => {
        const filename = documentFile.name;

        // Initialize progress for this document
        startNewFile(filename, index, total);

        // Set all steps to active (processing happening on backend)
        setStepStatus('uploading', 'active');
        setStepStatus('processing', 'active');
        setStepStatus('indexing', 'active');
        updateCurrentFileProgress({
            currentStep: 'uploading'
        });

        try {
            // Single call - backend does everything
            const uploadResp = await submitAssistantFile(
                assistantId,
                documentFile,
                idToken
            );

            if (!uploadResp.ok) {
                // Mark all active steps as error
                setStepStatus('uploading', 'error');
                setStepStatus('processing', 'error');
                setStepStatus('indexing', 'error');
                updateCurrentFileProgress({
                    currentStep: 'error',
                    errorMessage: uploadResp.data?.errors?.[0]?.error || 'Upload failed'
                });
                return {
                    success: false,
                    error: uploadResp.data?.errors?.[0]?.error || 'Upload failed'
                };
            }

            // Check for errors in response
            if (uploadResp.data?.errors?.length) {
                setStepStatus('uploading', 'error');
                setStepStatus('processing', 'error');
                setStepStatus('indexing', 'error');
                updateCurrentFileProgress({
                    currentStep: 'error',
                    errorMessage: uploadResp.data.errors[0].error
                });
                return {
                    success: false,
                    error: uploadResp.data.errors[0].error
                };
            }

            // All steps complete
            setStepStatus('uploading', 'complete');
            setStepStatus('processing', 'complete');
            setStepStatus('indexing', 'complete');
            updateCurrentFileProgress({
                currentStep: 'complete'
            });

            // Wait 1.5 seconds to show completion state
            await new Promise(resolve => setTimeout(resolve, 1500));

            return { success: true };

        } catch (error) {
            setStepStatus('uploading', 'error');
            setStepStatus('processing', 'error');
            setStepStatus('indexing', 'error');
            updateCurrentFileProgress({
                currentStep: 'error',
                errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    };

    const resetProgress = () => {
        setCurrentFileProgress(null);
        setCurrentFileIndex(0);
        setTotalFiles(0);
    };

    return {
        currentFileProgress,
        currentFileIndex,
        totalFiles,
        uploadDocument,
        resetProgress
    };
};